import { useMemo } from 'react';

import { Alert, Box } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Block, OneColumn } from 'components/page';
import { useLoaderContext } from 'context/LoaderContext';
import { useFocusErrorFormik } from 'hooks/useFocusErrorFormik';
import {
    DreFormBBar,
    DreFormCustos,
    DreFormDespesas,
    DreFormDespesasExtras,
    DreFormImpostos,
    DreFormMenu,
    DreFormTitle,
    DreFormVendas,
    DreLoadF360Btn,
} from 'pages/financeiro/dre/components';
import { useDreFormContext } from 'pages/financeiro/dre/contexts';
import { useQueryDre } from 'pages/financeiro/dre/hooks';
import { getErrorMessage } from 'util/error';

export function DreFormComp() {
    const { focusError } = useFocusErrorFormik();
    const { dre, dreStatus, status } = useDreFormContext();
    const { isFetching, isError, error } = useQueryDre();
    const [errorRequest] = getErrorMessage(error);
    const showF360Btn = Boolean(
        dre.loja?.lojaConfiguracoes?.some((config) => config?.configuracaoPropriedade.dsChave === 'cdLojaF360' && config?.dsValor),
    );

    const validationSchema = useMemo(
        () =>
            Yup.object({
                dreMercadologicaDespesasVariaveis: Yup.array().of(
                    Yup.object().shape({
                        dsDespesa: Yup.string().min(2).max(200).required(),
                        vlTotal: Yup.number().required(),
                    }),
                ),
            }),
        [],
    );

    useLoaderContext(Boolean(status));

    return (
        <OneColumn title="Preenchimento de Estimativa de Resultado" goBackButton="/financeiro/dre" extraContent={showF360Btn && <DreLoadF360Btn />}>
            <Formik enableReinitialize initialValues={dre} validationSchema={validationSchema} onSubmit={() => {}}>
                {({ errors, values }) => {
                    focusError(errors);

                    return (
                        <>
                            <Box
                                sx={{
                                    gap: 2,
                                    display: 'flex',
                                    alignItems: {
                                        xs: 'stretch',
                                        md: 'flex-start',
                                    },
                                    flexDirection: {
                                        xs: 'column',
                                        md: 'row',
                                    },
                                }}
                            >
                                <DreFormMenu />

                                <Box sx={{ flex: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {isError && errorRequest && <Alert severity="error">{errorRequest}</Alert>}

                                    {dreStatus === 204 && (
                                        <Alert severity="info">
                                            Estimativa de resultado para o mês e empresa escolhidos ainda não está disponivel para preenchimento
                                        </Alert>
                                    )}

                                    {isFetching && (
                                        <Block sx={{ display: 'flex', flexDirection: 'column', padding: 0, margin: 0 }}>
                                            <DreFormTitle label="" />

                                            <Box sx={{ padding: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {Array.from({ length: 10 }).map((_, index) => (
                                                    <Box
                                                        key={index}
                                                        className="pulse"
                                                        sx={{
                                                            height: 54,
                                                            borderWidth: 1,
                                                            borderRadius: 1,
                                                            borderColor: 'grey.300',
                                                            backgroundColor: 'grey.200',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Block>
                                    )}

                                    {dreStatus === 200 && !isFetching && (
                                        <>
                                            {Boolean(values.dreMercadologicaVendas?.length) && <DreFormVendas />}

                                            {Boolean(values.dreMercadologicaCustos?.length) && <DreFormCustos />}

                                            {Boolean(values.dreMercadologicaDespesas?.length) && <DreFormDespesas />}

                                            <DreFormDespesasExtras />

                                            {Boolean(values.dreMercadologicaImpostos?.length) && <DreFormImpostos />}
                                        </>
                                    )}
                                </Box>
                            </Box>

                            <DreFormBBar />
                        </>
                    );
                }}
            </Formik>
        </OneColumn>
    );
}
